// src/context/DealContext.js
import React, { createContext, useContext, useEffect, useState } from "react";
import api from "../services/api";
import { useAuth } from "./AuthContext";
import { useRBAC } from "../hooks/useRBAC";

// Create Context
const DealContext = createContext();

// Provider Component
export const DealProvider = ({ children }) => {
  const [Deals, setDeals] = useState([]);
  const { isAuthenticated, authVersion } = useAuth();
  const { getAccessibleDealIds } = useRBAC();

  // Fetch Deals from API. Deal DTOs are stored as returned by the backend — all fields,
  // including privacy/ownership (isPrivate, createdByUserId), pass through untouched to
  // consumers of useDeals(). The backend already hides private deals from non-members.
  const fetchDeals = async () => {
    try {
      const response = await api.get("/Deals");
      const allDeals = response.data;
      // Filter deals by RBAC accessible deal IDs
      const accessibleDealIds = getAccessibleDealIds();
      const filteredDeals = accessibleDealIds === null
        ? allDeals
        : allDeals.filter(d => accessibleDealIds.includes(d.dealId || d.DealId || d.dealID));
      setDeals(filteredDeals);
    } catch (error) {}
  };

  // Update Deal
  const updateDeal = async (id, updatedDeal) => {
    try {
      const res = await api.put(`/Deals/${id}`, updatedDeal);
      setDeals((prev) =>
        prev.map((r) => (r.DealId === id ? res.data : r))
      );
    } catch (error) {}
  };

  // Delete Deal
  const deleteDeal = async (id) => {
    try {
      await api.delete(`/Deals/${id}`);
      setDeals((prev) => prev.filter((r) => r.DealId !== id));
    } catch (error) {}
  };

  // Note: Auto-fetch removed to prevent race condition with organization sync.
  // Components should fetch deals after useOrganizationSync confirms isReady.

  return (
    <DealContext.Provider value={{ Deals, updateDeal, deleteDeal }}>
      {children}
    </DealContext.Provider>
  );
};

// Custom hooks
export const useDeals = () => {
  const { Deals } = useContext(DealContext);
  return Deals;
};

export const useDealActions = () => {
  const { updateDeal, deleteDeal } = useContext(DealContext);
  return { updateDeal, deleteDeal };
};
